<template>
  <div>
    <!-- On mobile -->
    <div class="flex justify-center md:hidden">
      <RevLink
        class="body-1-link"
        :tracking="{
          zone: TRACKING_ZONES.POLICY_DETAILS,
          name: 'see-coverage-details',
        }"
        @click="openModal(MODAL_NAMES.COVERAGE_DETAILS_DRAWER)"
      >
        {{ i18n(translations.seeCoverageDetails) }}
      </RevLink>
    </div>

    <RevDialog
      :name="MODAL_NAMES.COVERAGE_DETAILS_DRAWER"
      :title="i18n(translations.coverageDetailsDrawerTitle)"
    >
      <template #body>
        <ul class="divide-static-default-low space-y-12 divide-y">
          <li v-for="benefit in benefits" :key="benefit">
            <div class="my-12 flex gap-8">
              <div class="min-w-[24px]">
                <IconCheckInCircleFilled class="text-static-success-hi" />
              </div>
              <div class="body-1">{{ benefit }}</div>
            </div>
          </li>
        </ul>

        <div
          v-if="compliancyDocuments.length > 0"
          class="flex items-center mt-24 flex-col gap-4"
        >
          <RevLink
            v-if="termsAndConditionsDocument"
            class="body-1-link"
            target="_blank"
            :to="termsAndConditionsDocument.url"
          >
            {{ i18n(translations.downloadTermsAndConditions) }}
          </RevLink>

          <RevLink
            v-if="ipidDocument"
            class="body-1-link"
            target="_blank"
            :to="ipidDocument.url"
          >
            {{ i18n(translations.downloadIpid) }}
          </RevLink>

          <RevLink
            v-if="backmarketRecapDocument"
            class="body-1-link"
            target="_blank"
            :to="backmarketRecapDocument.url"
          >
            {{ i18n(translations.downloadBackmarketRecap) }}
          </RevLink>
        </div>
      </template>
    </RevDialog>

    <!-- On desktop -->
    <div class="hidden md:block">
      <RevDivider class="relative -inset-x-[32px] w-[calc(100%+64px)]" />
      <RevAccordionItem
        @click="
          track.trackClick({
            name: 'open-coverage-details-accordion',
            zone: TRACKING_ZONES.POLICY_DETAILS,
          })
        "
      >
        <template #title>
          <div class="body-1 ml-8">
            {{ i18n(translations.seeCoverageDetails) }}
          </div>
        </template>
        <template #body>
          <ul class="divide-static-default-low divide-y">
            <li v-for="benefit in benefits" :key="benefit">
              <div class="my-12 flex gap-8">
                <div class="min-w-[24px]">
                  <IconCheckInCircleFilled class="text-static-success-hi" />
                </div>
                <div class="body-1">{{ benefit }}</div>
              </div>
            </li>
          </ul>

          <div
            v-if="compliancyDocuments.length > 0"
            class="flex items-center mt-24 flex-col gap-4"
          >
            <RevLink
              v-if="termsAndConditionsDocument"
              class="body-1-link"
              target="_blank"
              :to="termsAndConditionsDocument.url"
            >
              {{ i18n(translations.downloadTermsAndConditions) }}
            </RevLink>

            <RevLink
              v-if="ipidDocument"
              class="body-1-link"
              target="_blank"
              :to="ipidDocument.url"
            >
              {{ i18n(translations.downloadIpid) }}
            </RevLink>

            <RevLink
              v-if="backmarketRecapDocument"
              class="body-1-link"
              target="_blank"
              :to="backmarketRecapDocument.url"
            >
              {{ i18n(translations.downloadBackmarketRecap) }}
            </RevLink>
          </div>
        </template>
      </RevAccordionItem>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevDialog } from '@ds/components/Dialog'
import { RevDivider } from '@ds/components/Divider'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { IconCheckInCircleFilled } from '@ds/icons/IconCheckInCircleFilled'

import {
  MODAL_NAMES,
  TRACKING_ZONES,
} from '~/scopes/dashboard/services-hub/constant'

import translations from './CoverageDetails.translations'

const i18n = useI18n()
const track = useTracking()

const props = defineProps<{
  benefits: string[]
  compliancyDocuments: ApiSpecs['/api/insurance/v1/services-hub/policies/:policyId']['GET']['response']['policy']['offer']['compliancyDocuments']
}>()

const termsAndConditionsDocument = computed(() => {
  return props.compliancyDocuments.find(
    (document) => document.documentType === 'TERMS_AND_CONDITIONS',
  )
})

const ipidDocument = computed(() => {
  return props.compliancyDocuments.find(
    (document) => document.documentType === 'IPID',
  )
})

const backmarketRecapDocument = computed(() => {
  return props.compliancyDocuments.find(
    (document) => document.documentType === 'BACKMARKET_RECAP',
  )
})
</script>
